import { html } from "lit";
import type { TemplateResult } from "lit";

import { basePlatformConfigs, type SupportedPlatform } from "./socialMediaBaseConfig.js";

import appleMusic from "./social-media-icons/apple-music.png";
import berealIcon from "./social-media-icons/bereal.png";
import discordIcon from "./social-media-icons/discord.png";
import facebookIcon from "./social-media-icons/facebook.png";
import instagramIcon from "./social-media-icons/instagram.png";
import linkedinIcon from "./social-media-icons/linkedin.png";
import pinterestIcon from "./social-media-icons/pinterest.png";
import podimoIcon from "./social-media-icons/podimo.png";
import polarstepsIcon from "./social-media-icons/polarsteps.png";
import snapchatIcon from "./social-media-icons/snapchat.png";
import soundcloudIcon from "./social-media-icons/soundcloud.png";
import spotifyIcon from "./social-media-icons/spotify.png";
import threadsIcon from "./social-media-icons/threads.png";
import tiktokIcon from "./social-media-icons/tiktok.png";
import twitchIcon from "./social-media-icons/twitch.png";
import twitterIcon from "./social-media-icons/twitter.png";
import vintedIcon from "./social-media-icons/vinted.png";
import youtubeIcon from "./social-media-icons/youtube.png";
import emailIcon from "./social-media-icons/email.png";

export interface SocialPlatformConfig {
  platform: SupportedPlatform;
  platformName: string;
  colorfulIcon: string;
  blackAndWhiteIcon: TemplateResult;
  domains: string[];
  validation: (url: string) => boolean;
}

const iconMap: Record<SupportedPlatform, { colorfulIcon: string; blackAndWhiteIcon: TemplateResult }> = {
  instagram: {
    colorfulIcon: instagramIcon,
    blackAndWhiteIcon: html`<ph-instagram-logo size="16" weight="fill"></ph-instagram-logo>`,
  },
  tiktok: {
    colorfulIcon: tiktokIcon,
    blackAndWhiteIcon: html`<ph-tiktok-logo size="16" weight="fill"></ph-tiktok-logo>`,
  },
  youtube: {
    colorfulIcon: youtubeIcon,
    blackAndWhiteIcon: html`<ph-youtube-logo size="16" weight="fill"></ph-youtube-logo>`,
  },
  snapchat: {
    colorfulIcon: snapchatIcon,
    blackAndWhiteIcon: html`<ph-snapchat-logo size="16" weight="fill"></ph-snapchat-logo>`,
  },
  pinterest: {
    colorfulIcon: pinterestIcon,
    blackAndWhiteIcon: html`<ph-pinterest-logo size="16" weight="fill"></ph-pinterest-logo>`,
  },
  x: {
    colorfulIcon: twitterIcon,
    blackAndWhiteIcon: html`<ph-x-logo size="16" weight="fill"></ph-x-logo>`,
  },
  twitch: {
    colorfulIcon: twitchIcon,
    blackAndWhiteIcon: html`<ph-twitch-logo size="16" weight="fill"></ph-twitch-logo>`,
  },
  linkedin: {
    colorfulIcon: linkedinIcon,
    blackAndWhiteIcon: html`<ph-linkedin-logo size="16" weight="fill"></ph-linkedin-logo>`,
  },
  facebook: {
    colorfulIcon: facebookIcon,
    blackAndWhiteIcon: html`<ph-facebook-logo size="16" weight="fill"></ph-facebook-logo>`,
  },
  spotify: {
    colorfulIcon: spotifyIcon,
    blackAndWhiteIcon: html`<ph-spotify-logo size="16" weight="fill"></ph-spotify-logo>`,
  },
  soundcloud: {
    colorfulIcon: soundcloudIcon,
    blackAndWhiteIcon: html`<ph-soundcloud-logo size="16" weight="fill"></ph-soundcloud-logo>`,
  },
  podimo: {
    colorfulIcon: podimoIcon,
    blackAndWhiteIcon: html`<svg viewBox="0 0 48 48" fill="currentColor" width="16" height="16">
      <path
        class="b"
        d="m22.9459,35.744V6.0382c0-.8495.6887-1.5382,1.5382-1.5382h0c8.5966,0,15.5655,6.9689,15.5655,15.5655v1.6512c0,8.5966-6.9689,15.5655-15.5655,15.5655h0c-.8495,0-1.5382-.6887-1.5382-1.5382Z"
      />
      <path
        class="b"
        d="m7.9504,27.2169V5.6184c0-.6177.5007-1.1184,1.1184-1.1184h0c6.2504,0,11.3174,5.067,11.3174,11.3174v1.2006c0,6.2504-5.067,11.3174-11.3174,11.3174h0c-.6177,0-1.1184-.5007-1.1184-1.1184Z"
      />
      <circle class="b" cx="14.1683" cy="37.2821" r="6.2179" />
    </svg>`,
  },
  "apple-music": {
    colorfulIcon: appleMusic,
    blackAndWhiteIcon: html`<svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
      >
      <path
        d="m24 6.124c0-.029.001-.063.001-.097 0-.743-.088-1.465-.253-2.156l.013.063c-.312-1.291-1.1-2.359-2.163-3.031l-.02-.012c-.536-.35-1.168-.604-1.847-.723l-.03-.004c-.463-.084-1.003-.138-1.553-.15h-.011c-.04 0-.083-.01-.124-.013h-12.025c-.152.01-.3.017-.455.026-.791.016-1.542.161-2.242.415l.049-.015c-1.306.501-2.327 1.495-2.853 2.748l-.012.033c-.17.409-.297.885-.36 1.38l-.003.028c-.051.343-.087.751-.1 1.165v.016c0 .032-.007.062-.01.093v12.224c.01.14.017.283.027.424.02.861.202 1.673.516 2.416l-.016-.043c.609 1.364 1.774 2.387 3.199 2.792l.035.009c.377.111.817.192 1.271.227l.022.001c.555.053 1.11.06 1.667.06h11.028c.554 0 1.099-.037 1.633-.107l-.063.007c.864-.096 1.645-.385 2.321-.823l-.021.013c.825-.539 1.47-1.29 1.867-2.176l.013-.032c.166-.383.295-.829.366-1.293l.004-.031c.084-.539.132-1.161.132-1.794 0-.086-.001-.171-.003-.256v.013q0-5.7 0-11.394zm-6.424 3.99v5.712c.001.025.001.054.001.083 0 .407-.09.794-.252 1.14l.007-.017c-.273.562-.771.979-1.373 1.137l-.015.003c-.316.094-.682.156-1.06.173h-.01c-.029.002-.062.002-.096.002-1.033 0-1.871-.838-1.871-1.871 0-.741.431-1.382 1.056-1.685l.011-.005c.293-.14.635-.252.991-.32l.027-.004c.378-.082.758-.153 1.134-.24.264-.045.468-.252.51-.513v-.003c.013-.057.02-.122.02-.189 0-.002 0-.003 0-.005q0-2.723 0-5.443c-.001-.066-.01-.13-.027-.19l.001.005c-.026-.134-.143-.235-.283-.235-.006 0-.012 0-.018.001h.001c-.178.013-.34.036-.499.07l.024-.004q-1.14.225-2.28.456l-3.7.748c-.016 0-.032.01-.048.013-.222.03-.392.219-.392.447 0 .015.001.03.002.045v-.002.13q0 3.9 0 7.801c.001.028.001.062.001.095 0 .408-.079.797-.224 1.152l.007-.021c-.264.614-.792 1.072-1.436 1.235l-.015.003c-.319.096-.687.158-1.067.172h-.008c-.031.002-.067.003-.104.003-.913 0-1.67-.665-1.815-1.536l-.001-.011c-.02-.102-.031-.218-.031-.338 0-.785.485-1.458 1.172-1.733l.013-.004c.315-.127.687-.234 1.072-.305l.036-.005c.287-.06.575-.116.86-.177.341-.05.6-.341.6-.693 0-.007 0-.015 0-.022v.001-.15q0-4.44 0-8.883c0-.002 0-.004 0-.007 0-.129.015-.254.044-.374l-.002.011c.066-.264.277-.466.542-.517l.004-.001c.255-.066.515-.112.774-.165.733-.15 1.466-.3 2.2-.444l2.27-.46c.67-.134 1.34-.27 2.01-.4.181-.042.407-.079.637-.104l.027-.002c.018-.002.04-.004.061-.004.27 0 .489.217.493.485.008.067.012.144.012.222v.001q0 2.865 0 5.732z"
      />
    </svg>`,
  },
  vinted: {
    colorfulIcon: vintedIcon,
    blackAndWhiteIcon: html`<svg fill="currentColor" width="16" height="16" viewBox="0 0 48 48">
      <path
        class="a"
        d="M16.23,5.82c4.69-.69,5.11,2.25,5,5.36,0,15,.24,24.84.82,25.37,4.07-9.75,7.45-25.5,9.45-28.34A10.2,10.2,0,0,1,34,5.72c1.2-.76,2.57-1.48,2.91-1.12-.14,4.52-8,30.84-8.37,31.8A12.09,12.09,0,0,1,21.65,43c-6.39,1.52-6.82-.87-7.63-2.21-1.89-4.1-3.25-28.36-2.78-31C11.34,8.77,11.56,6.11,16.23,5.82Z"
      />
    </svg>`,
  },
  threads: {
    colorfulIcon: threadsIcon,
    blackAndWhiteIcon: html`<svg aria-label="Threads" fill="currentColor" width="16" height="16" viewBox="0 0 192 192">
      <path
        class="x19hqcy"
        d="M141.537 88.9883C140.71 88.5919 139.87 88.2104 139.019 87.8451C137.537 60.5382 122.616 44.905 97.5619 44.745C97.4484 44.7443 97.3355 44.7443 97.222 44.7443C82.2364 44.7443 69.7731 51.1409 62.102 62.7807L75.881 72.2328C81.6116 63.5383 90.6052 61.6848 97.2286 61.6848C97.3051 61.6848 97.3819 61.6848 97.4576 61.6855C105.707 61.7381 111.932 64.1366 115.961 68.814C118.893 72.2193 120.854 76.925 121.825 82.8638C114.511 81.6207 106.601 81.2385 98.145 81.7233C74.3247 83.0954 59.0111 96.9879 60.0396 116.292C60.5615 126.084 65.4397 134.508 73.775 140.011C80.8224 144.663 89.899 146.938 99.3323 146.423C111.79 145.74 121.563 140.987 128.381 132.296C133.559 125.696 136.834 117.143 138.28 106.366C144.217 109.949 148.617 114.664 151.047 120.332C155.179 129.967 155.42 145.8 142.501 158.708C131.182 170.016 117.576 174.908 97.0135 175.059C74.2042 174.89 56.9538 167.575 45.7381 153.317C35.2355 139.966 29.8077 120.682 29.6052 96C29.8077 71.3178 35.2355 52.0336 45.7381 38.6827C56.9538 24.4249 74.2039 17.11 97.0132 16.9405C119.988 17.1113 137.539 24.4614 149.184 38.788C154.894 45.8136 159.199 54.6488 162.037 64.9503L178.184 60.6422C174.744 47.9622 169.331 37.0357 161.965 27.974C147.036 9.60668 125.202 0.195148 97.0695 0H96.9569C68.8816 0.19447 47.2921 9.6418 32.7883 28.0793C19.8819 44.4864 13.2244 67.3157 13.0007 95.9325L13 96L13.0007 96.0675C13.2244 124.684 19.8819 147.514 32.7883 163.921C47.2921 182.358 68.8816 191.806 96.9569 192H97.0695C122.03 191.827 139.624 185.292 154.118 170.811C173.081 151.866 172.51 128.119 166.26 113.541C161.776 103.087 153.227 94.5962 141.537 88.9883ZM98.4405 129.507C88.0005 130.095 77.1544 125.409 76.6196 115.372C76.2232 107.93 81.9158 99.626 99.0812 98.6368C101.047 98.5234 102.976 98.468 104.871 98.468C111.106 98.468 116.939 99.0737 122.242 100.233C120.264 124.935 108.662 128.946 98.4405 129.507Z"
      ></path>
    </svg> `,
  },
  bereal: {
    colorfulIcon: berealIcon,
    blackAndWhiteIcon: html`<svg
      fill="none"
      stroke="currentColor"
      stroke-linecap="round"
      stroke-linejoin="round"
      width="16"
      height="16"
      viewBox="0 0 48 48"
      id="a"
    >
      <path
        class="b"
        d="M40.5,5.5H7.5c-1.1046,0-2,.8954-2,2V40.5c0,1.1046,.8954,2,2,2H40.5c1.1046,0,2-.8954,2-2V7.5c0-1.1046-.8954-2-2-2Z"
      />
      <g>
        <g>
          <path
            class="b"
            d="M20.8368,27.7715v-7.5429h2.4694c1.3961,0,2.5278,1.1342,2.5278,2.5332s-1.1317,2.5332-2.5278,2.5332h-2.4694"
          />
          <line class="b" x1="23.3062" y1="25.295" x2="25.7754" y2="27.7696" />
        </g>
        <path
          class="b"
          d="M18.7569,26.8198c-.3248,.5685-.9369,.9516-1.6385,.9516h0c-1.0415,0-1.8857-.8443-1.8857-1.8857v-1.2257c0-1.0415,.8443-1.8857,1.8857-1.8857h0c1.0415,0,1.8857,.8443,1.8857,1.8857v.6129s-3.7715,0-3.7715,0"
        />
        <path
          class="b"
          d="M30.8201,26.8198c-.3248,.5685-.9369,.9516-1.6385,.9516h0c-1.0415,0-1.8857-.8443-1.8857-1.8857v-1.2257c0-1.0415,.8443-1.8857,1.8857-1.8857h0c1.0415,0,1.8857,.8443,1.8857,1.8857v.6129s-3.7715,0-3.7715,0"
        />
        <path class="b" d="M38.2743,20.2285v6.6001c0,.5207,.4221,.9429,.9429,.9429h.2829" />
        <g>
          <path
            class="b"
            d="M36.5489,25.8857c0,1.0415-.8443,1.8857-1.8857,1.8857h0c-1.0415,0-1.8857-.8443-1.8857-1.8857v-1.2257c0-1.0415,.8443-1.8857,1.8857-1.8857h0c1.0415,0,1.8857,.8443,1.8857,1.8857"
          />
          <line class="b" x1="36.5489" y1="27.7715" x2="36.5489" y2="22.7743" />
        </g>
        <g>
          <path
            class="b"
            d="M11.6115,24c1.0415,0,1.8857,.8443,1.8857,1.8857s-.8443,1.8857-1.8857,1.8857h-3.1115v-7.5429h3.1115c1.0415,0,1.8857,.8443,1.8857,1.8857s-.8443,1.8857-1.8857,1.8857h0Z"
          />
          <line class="b" x1="11.6115" y1="24" x2="8.5" y2="24" />
        </g>
      </g>
    </svg>`,
  },
  polarsteps: {
    colorfulIcon: polarstepsIcon,
    blackAndWhiteIcon: html`<svg
      fill="none"
      stroke="currentColor"
      stroke-linecap="round"
      stroke-linejoin="round"
      width="16"
      height="16"
      viewBox="0 0 48 48"
      id="b"
    >
      <circle class="c" cx="24" cy="24" r="21.5" />
      <path
        class="c"
        d="m22.8,21.2c1-.4,2.1-.3,3,.3.2.1.4.2.6.1l2.4-1c.5-.2.7-.9.3-1.3l-10.3-10.4c-.7-.7-2-.2-2,.8l.2,14.5c0,.6.6,1,1.1.7l2.4-1c.2-.1.3-.3.4-.5.2-.9.9-1.8,1.9-2.2Z"
      />
      <path
        class="c"
        d="m25.2,26.8c1-.4,1.7-1.3,1.8-2.4,0-.2.2-.4.4-.5l2.4-1c.5-.2,1.1.2,1.1.7l.2,14.5c0,1-1.2,1.6-2,.8l-10.1-10.1c-.4-.4-.3-1.1.3-1.3l2.4-1c.2-.1.4,0,.6.1.8.5,1.9.7,2.9.2Z"
      />
    </svg>`,
  },
  discord: {
    colorfulIcon: discordIcon,
    blackAndWhiteIcon: html`<ph-discord-logo size="16" weight="fill"></ph-discord-logo>`,
  },
  email: {
    colorfulIcon: emailIcon,
    blackAndWhiteIcon: html`<ph-envelope size="16" weight="fill"></ph-envelope>`,
  },
};

export const platformConfigs: Array<SocialPlatformConfig> = basePlatformConfigs.map((baseConfig) => ({
  ...baseConfig,
  ...iconMap[baseConfig.platform],
}));

export { type SupportedPlatform };

export interface ParsedSocialLink {
  platform: SupportedPlatform | "other";
  platformName: string;
  icon: string;
  originalUrl: string;
}

export function parseSocialMediaUrl(url: string): ParsedSocialLink | undefined {
  try {
    if (!url.startsWith("http")) {
      const emailConfig = platformConfigs.find((p) => p.platform === "email");
      if (emailConfig) {
        // Treat all non-http URLs as email inputs (including partial typing)
        return {
          platform: emailConfig.platform,
          platformName: emailConfig.platformName,
          icon: emailConfig.colorfulIcon,
          originalUrl: url || "",
        };
      }
    } else {
      const parsedUrl = new URL(url);
      const hostname = parsedUrl.hostname.replace(/^www\./, "");

      const platformConfig = platformConfigs.find((config) => config.domains.includes(hostname));

      if (platformConfig) {
        return {
          platform: platformConfig.platform,
          platformName: platformConfig.platformName,
          icon: platformConfig.colorfulIcon,
          originalUrl: url,
        };
      }
    }
  } catch {
    return {
      platform: "other",
      icon: "",
      platformName: "Unknown",
      originalUrl: url,
    };
  }
}

export function parseSocialMediaUrls(urls: string[]): ParsedSocialLink[] {
  return urls.map(parseSocialMediaUrl).filter(Boolean);
}

export function getUsernamePrefix(platform: string) {
  switch (platform) {
    case "tiktok":
    case "snapchat":
    case "threads":
    case "youtube":
      return "@";
    case "linkedin":
      return "in/";
    case "vinted":
      return "member/";
    case "discord":
      return "users/";
    default:
      return "";
  }
}
